import React from "react";
import { ArrowUpRight, Leaf, Sprout, Truck } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { ScrollVideoHero } from "../components/ScrollVideoHero";
import { BrandMarquee } from "../components/BrandMarquee";
import { HOW_IT_WORKS, TRUST_STRIP, IMAGES, waLink } from "../lib/data";

const HowIcon = ({ i }) => {
  const cls = "w-7 h-7 text-brand-sprout";
  if (i === 0) return <Leaf className={cls} />;
  if (i === 1) return <Sprout className={cls} />;
  return <Truck className={cls} />;
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — scroll-scrubbed video */}
      <ScrollVideoHero />

      {/* WHY MICROGREENS */}
      <section className="relative bg-brand-dark text-brand-bg py-24 md:py-36 px-5 md:px-10 overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-2">
            <span className="font-mono text-[7rem] md:text-[10rem] leading-none font-medium text-brand-sprout">40</span>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
              Why microgreens
            </p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight">
              Up to 40x the nutrients of mature vegetables.
            </h2>
            <p className="mt-6 text-brand-bg/70 text-base md:text-lg leading-relaxed max-w-xl">
              Vitamins, antioxidants, and minerals concentrated in every tiny leaf. That's where our
              name comes from — nutrition, distilled to its most powerful form.
            </p>
          </Reveal>
          <Reveal delay={0.25} className="md:col-span-4">
            <div className="overflow-hidden rounded-[1.5rem] rounded-bl-[5rem] h-72 md:h-96">
              <img src={IMAGES.peaShoots} alt="Pea shoots" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-36 px-5 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-brand-sprout mb-6">
              How it works
            </p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-brand-ink max-w-2xl leading-tight">
              Three steps between you and the freshest greens.
            </h2>
          </Reveal>

          <StaggerGroup className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((step, i) => (
              <StaggerItem key={step.n}>
                <div className="group h-full bg-brand-card rounded-[1.5rem] p-8 md:p-10 border border-black/5 transition-transform duration-500 hover:-translate-y-2">
                  <div className="flex items-center justify-between mb-8">
                    <span className="font-mono text-sm text-brand-muted">{step.n}</span>
                    <HowIcon i={i} />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl text-brand-ink leading-tight mb-4">
                    {step.title}
                  </h3>
                  <p className="text-brand-muted leading-relaxed">{step.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <BrandMarquee />

      {/* TRUST STRIP */}
      <section className="py-24 md:py-32 px-5 md:px-10">
        <div className="max-w-[1400px] mx-auto">
          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 rounded-[1.5rem] overflow-hidden border border-black/10">
            {TRUST_STRIP.map((item, i) => (
              <StaggerItem key={i}>
                <div className="bg-brand-bg h-full p-8 md:p-10">
                  <span className="font-mono text-xs text-brand-sprout">0{i + 1}</span>
                  <p className="mt-4 font-serif text-xl md:text-2xl text-brand-ink leading-snug">
                    {item}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="px-5 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="relative max-w-[1400px] mx-auto rounded-[2rem] overflow-hidden bg-brand-forest text-brand-bg px-8 py-16 md:px-20 md:py-28">
            <div className="noise-overlay" />
            <div className="relative">
              <h2 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] max-w-3xl">
                Your greens shouldn't travel further than you do.
              </h2>
              <p className="mt-6 text-brand-bg/70 text-lg max-w-xl">
                Find your local 40X Leaves grower today.
              </p>
              <a
                href={waLink("Hi 40X Leaves, I'd like to find my local grower. My pincode is ")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-cta-whatsapp"
                className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand-bg text-brand-forest px-8 py-4 text-sm font-semibold transition-transform duration-300 hover:scale-105"
              >
                Get started on WhatsApp
                <ArrowUpRight size={18} />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
